import {Table, TablePaginationConfig, Tooltip} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import moment from 'moment/moment';
import React from 'react';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';
import {
  ChargeFieldsFragment,
  ChargeStatus,
  Maybe,
  PaymentMethod,
  Processor
} from '../../api/generated';
import {PaymentMethodOverview} from '../../components';
import {ChargeStatusTag} from '../../components/ChargeStatusTag';
import {useAccessLevel} from '../../hooks/useAccessLevel';
import {getChargeCustomer} from '../../lib/chargeUtils';
import {shortProcessorLabels} from '../../lib/labels';
import {dateFormat, truncate} from '../../lib/utils';

const Amount = styled.span`
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
`;

const PaymentMethodIcon = styled.div`
  opacity: 0.8;
  overflow: visible;
`;

const ChargesTable = styled(Table)`
  td {
    vertical-align: top;
  }
` as unknown as typeof Table;

type Props = {
  payments: ChargeFieldsFragment[];
  loading: boolean;
  accountId?: string;
  pagination?: false | TablePaginationConfig;
};
export const PaymentsTable: React.FC<Props> = ({payments, loading, accountId, pagination}) => {
  const history = useHistory();
  const {isAdmin} = useAccessLevel();
  const columns: Array<ColumnProps<ChargeFieldsFragment>> = [
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      className: 'minimized-cell',
      render(amount: number, charge) {
        const currency = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: charge.currency
        });
        return <Amount>{currency.format(amount / 100)}</Amount>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'minimized-cell',
      render(status: ChargeStatus, charge) {
        return <ChargeStatusTag charge={charge} />;
      }
    },
    {
      title: accountId ? 'Order ID' : 'Store',
      dataIndex: accountId ? 'orderId' : ['shop', 'name'],
      render(value) {
        return <Tooltip title={value}>{truncate(value, 20)}</Tooltip>;
      }
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'email'],
      render(email: string, charge) {
        const customer = getChargeCustomer(charge) as string;
        return <Tooltip title={customer}>{truncate(customer, 25)}</Tooltip>;
      }
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      className: 'minimized-cell',
      render(_: Maybe<PaymentMethod>, charge: ChargeFieldsFragment) {
        return (
          <PaymentMethodIcon>
            <PaymentMethodOverview item={charge} minimal />
          </PaymentMethodIcon>
        );
      }
    },
    ...(isAdmin
      ? [
          {
            title: 'Source',
            dataIndex: 'traceDetails',
            render(traceDetails: any) {
              return traceDetails?.source?.split('/').pop();
            }
          }
        ]
      : []),
    ...(isAdmin
      ? [
          {
            title: 'Processor',
            dataIndex: 'processor',
            render(processor: Processor) {
              return shortProcessorLabels[processor];
            }
          }
        ]
      : []),
    {
      title: 'Date',
      dataIndex: 'createdAt',
      className: 'minimized-cell',
      render(date: number) {
        const anHourAgo = moment().subtract(59, 'minutes');
        const fullDate = moment.unix(date).format('lll');
        const relativeDate = moment.max(moment.unix(date), anHourAgo).isSame(anHourAgo)
          ? moment.unix(date).format(dateFormat.dateTime)
          : moment.unix(date).fromNow();
        return (
          <Tooltip placement="topLeft" title={fullDate}>
            {relativeDate}
          </Tooltip>
        );
      }
    }
  ];

  return (
    <ChargesTable
      scroll={{x: true}}
      dataSource={payments}
      columns={columns}
      loading={loading}
      rowClassName={() => 'clickable'}
      rowKey="id"
      onRow={(charge) => ({
        onClick: (e) => {
          e.preventDefault();
          const url = `/payments/${charge.id}`;
          if (e.ctrlKey || e.metaKey) {
            window.open(url, charge.id)?.focus();
          } else {
            history.push(url);
          }
        }
      })}
      pagination={pagination}
    />
  );
};
